/**
 * Build the "<platform> <kind>" label shown next to a product on the
 * vulnerability and product pages.
 *
 * Platform and kind collide for first-party entries: WordPress core is
 * `platform=WordPress, kind=WordPress, product=wordpress`, and npm packages
 * are `platform=npm, kind=npm, product=<package>`. Printing both verbatim
 * reads "WordPress wordpress". Drupal already ships `core` as its kind, so
 * use that word when the product *is* the platform, and drop the bare repeat
 * otherwise.
 *
 * The core word follows the capitalisation of the kind it replaces, so it
 * matches its surroundings either way: the API's `kind_name` is title-cased
 * ("WordPress Core", next to "WordPress Plugin") while the route segment is
 * lower-cased ("WordPress core", next to "WordPress plugin").
 */
export const formatProductLabel = (
  platform: string | undefined | null,
  kind: string | undefined | null,
  product: string | undefined | null
): string => {
  const platformName = platform ?? '';
  const kindName = kind ?? '';

  if (!platformName) {
    return kindName;
  }

  if (kindName.toLowerCase() !== platformName.toLowerCase()) {
    return kindName ? `${platformName} ${kindName}` : platformName;
  }

  if ((product ?? '').toLowerCase() !== platformName.toLowerCase()) {
    return platformName;
  }

  const coreWord = /^[A-Z]/.test(kindName) ? 'Core' : 'core';
  return `${platformName} ${coreWord}`;
};

/**
 * The registry is the only ecosystem whose packages are named by themselves.
 *
 * WordPress and Drupal need the ecosystem and the kind to identify a product — a plugin and a
 * theme can share a name, and "Contact Form 7" alone says neither which site software it is for
 * nor what it is. An npm package name is already unique within npm and is how everyone refers to
 * it, so `npm openclaw npm` says the word twice and adds nothing to `openclaw`.
 */
const SELF_NAMING_PLATFORMS = ['npm'];

/**
 * How a product is named in a page title.
 *
 * Everywhere but npm this is the ecosystem, the product and its kind, with a repeated word
 * dropped: WordPress core arrives as `platform=WordPress, kind=WordPress, product=wordpress` and
 * would otherwise read "WordPress WordPress WordPress".
 */
export const formatProductTitle = (
  platform: string | undefined | null,
  name: string | undefined | null,
  kind: string | undefined | null
): string => {
  const productName = (name ?? '').trim();

  if (
    SELF_NAMING_PLATFORMS.includes((platform ?? '').trim().toLowerCase()) &&
    productName
  ) {
    return productName;
  }

  return [platform, productName, kind]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .filter(
      (part, index, parts) =>
        parts.findIndex(
          (other) => other.toLowerCase() === part.toLowerCase()
        ) === index
    )
    .join(' ');
};
